import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Header } from "../common/Header";
// import { SearchBar } from "./SearchBar";
// import { FilterSortBar } from "./FilterSortBar";
import { ProductGrid } from "./ProductGrid";
import { EmptyState } from "./EmptyState";
import { BottomNavigation } from "../ui/BottomNavigation";
import { useProducts } from "@nusrif/hooks";
import { useNavigate } from "react-router";
import { Spinner } from "../ui/Spinner";

export type SortOption =
  | "newest"
  | "price-low"
  | "price-high"
  | "popularity"
  | "rating";
export type CategoryFilter = "all" | "dresses" | "sneakers" | "accessories";

export const StorePage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<
    "oversize" | "box" | "regular"
  >("oversize");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFilter>("all");
  const [sortBy, _setSortBy] = useState<SortOption>("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore] = useState(false);
  const ITEMS_PER_PAGE = 50;

  // Memoize filters
  const filters = useMemo(() => {
    const f: any = { enabled: { eq: true } };
    if (searchQuery.trim()) f.name = { contains: searchQuery };
    if (selectedCategory !== "all")
      f["facetValues.code"] = { eq: selectedCategory };
    return f;
  }, [searchQuery, selectedCategory]);

  // Memoize sort
  const sort = useMemo(() => {
    switch (sortBy) {
      case "price-low":
        return { price: "ASC" as const };
      case "price-high":
        return { price: "DESC" as const };
      case "popularity":
        return { reviewCount: "DESC" as const };
      case "rating":
        return { rating: "DESC" as const };
      case "newest":
      default:
        return { createdAt: "DESC" as const };
    }
  }, [sortBy]);

  // Use the products API
  const {
    products,
    totalItems,
    isLoading: loading,
  } = useProducts({
    skip: 0,
    take: currentPage * ITEMS_PER_PAGE,
    filters,
    sort,
    enabled: true,
  });

  // Paginated products for display
  const displayedProducts = products;

  // Group products by fit into three sections (hardcoded titles for now)
  const { oversizeProducts, boxProducts, regularProducts } = useMemo(() => {
    const normalize = (value?: string) => (value || "").trim().toLowerCase();
    const result = {
      oversizeProducts: [] as typeof products,
      boxProducts: [] as typeof products,
      regularProducts: [] as typeof products,
    };
    for (const p of displayedProducts) {
      const fit = normalize(p.customFields?.fit);
      if (fit.includes("oversize") || fit.includes("oversized")) {
        result.oversizeProducts.push(p);
      } else if (fit.includes("box")) {
        result.boxProducts.push(p);
      } else if (fit.includes("regular")) {
        result.regularProducts.push(p);
      }
    }
    return result;
  }, [displayedProducts]);

  // Refs for sections to support programmatic scroll and active highlighting
  const oversizeRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const regularRef = useRef<HTMLDivElement | null>(null);

  // Observe sections to update activeSection as user scrolls
  useEffect(() => {
    const sections: Array<{
      key: "oversize" | "box" | "regular";
      ref: React.RefObject<HTMLDivElement | null>;
    }> = [
      { key: "oversize", ref: oversizeRef },
      { key: "box", ref: boxRef },
      { key: "regular", ref: regularRef },
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry most visible
        const sorted = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (sorted.length > 0) {
          const top = sorted[0];
          const matched = sections.find((s) => s.ref.current === top.target);
          if (matched) setActiveSection(matched.key);
        }
      },
      { root: null, threshold: [0.25, 0.5, 0.75] }
    );

    sections.forEach((s) => {
      if (s.ref.current) observer.observe(s.ref.current);
    });

    return () => observer.disconnect();
  }, []);

  

  // Tabs fixing on scroll like NewArrivals
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsAnchorYRef = useRef<number>(0);
  const [isTabsFixed, setIsTabsFixed] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!tabsContainerRef.current) return;
      const rect = tabsContainerRef.current.getBoundingClientRect();
      tabsAnchorYRef.current = window.scrollY + rect.top;
      setTabsHeight(tabsContainerRef.current.offsetHeight || 48);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const shouldFix = window.scrollY >= tabsAnchorYRef.current - 1;
      setIsTabsFixed(shouldFix);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = useCallback(
    (section: "oversize" | "box" | "regular") => {
      const map: Record<string, HTMLDivElement | null> = {
        oversize: oversizeRef.current,
        box: boxRef.current,
        regular: regularRef.current,
      };
      const node = map[section];
      if (node) {
        const topOffset = isTabsFixed ? tabsHeight : 0;
        const rect = node.getBoundingClientRect();
        const absoluteTop = window.scrollY + rect.top - topOffset;
        window.scrollTo({ top: absoluteTop, behavior: "smooth" });
        setActiveSection(section);
      }
    },
    [isTabsFixed, tabsHeight]
  );

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, sortBy]);

  // Handle search
  // const handleSearch = useCallback((query: string) => {
  //   setSearchQuery(query);
  // }, []);
  // // Handle category filter
  // const handleCategoryChange = useCallback((category: CategoryFilter) => {
  //   setSelectedCategory(category);
  // }, []);
  // // Handle sort change
  // const handleSortChange = useCallback((sort: SortOption) => {
  //   setSortBy(sort);
  // }, []);

  // Handle load more (infinite scroll simulation)
  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading) return;
    setCurrentPage((prev) => prev + 1);
  }, [hasMore, loading]);
  // Handle product click
  const handleProductClick = useCallback(
    (productId: number) => {
      navigate(`/product/${productId}`);
    },
    [navigate]
  );
  // Handle favorite click
  const handleFavoriteClick = useCallback((productId: number) => {
    console.log(`Toggle favorite for product ${productId}`);
    // Implement favorite logic here
  }, []);

  // Get category counts for filter display
  // const categoryCounts = useMemo(() => {
  //   const counts = products.reduce(
  //     (acc, product) => {
  //       // Use facetValues to count categories
  //       const categoryFacet = product.facetValues.find(
  //         (f) => f.facet.name.toLowerCase() === "category"
  //       );
  //       if (categoryFacet) {
  //         const code = categoryFacet.code.toLowerCase();
  //         acc[code] = (acc[code] || 0) + 1;
  //       }
  //       return acc;
  //     },
  //     {} as Record<string, number>
  //   );

  //   return {
  //     all: products.length,
  //     dresses: counts.dresses || 0,
  //     sneakers: counts.sneakers || 0,
  //     accessories: counts.accessories || 0,
  //   };
  // }, [products]);

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      {/* Fixed Header */}
      <Header title="Store" showBackButton={false} showCartButton={true} />

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto pb-[70px] pt-20">
        <div className="px-8 space-y-4">
          {/* Search Bar */}
          {/* <div className="sticky top-0 bg-gray-50 py-4 z-40">
            <SearchBar
              searchQuery={searchQuery}
              onSearch={handleSearch}
              placeholder="Search products, brands, categories..."
            />
          </div> */}
          {/* Filter and Sort Bar */}
          {/* <FilterSortBar
            selectedCategory={selectedCategory}
            sortBy={sortBy}
            onCategoryChange={handleCategoryChange}
            onSortChange={handleSortChange}
            categoryCounts={categoryCounts}
          /> */}
          {/* Header with tagline and optional actions */}
          <div className="flex items-end justify-between mt-8">
            <div className="space-y-1">
              <h1 className="text-base font-semibold text-black">Our Exclusive T‑Shirt Collection</h1>
              <p className="text-[11px] text-gray-500">Oversize, Box, and Regular fits curated for you</p>
            </div>
            {!loading && (
              <div className="pl-4">
                <p className="text-[11px] text-gray-500">
                  {totalItems} item{totalItems !== 1 ? "s" : ""}
                </p>
              </div>
            )}
          </div>

          {/* Segmented tab bar (becomes fixed on scroll) */}
          <div ref={tabsContainerRef} className="-mx-8">
            <div
              className={
                (isTabsFixed
                  ? "fixed inset-x-0 top-0 z-50 "
                  : "relative z-50 ") +
                "bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]"
              }
            >
              <div className="px-8 py-2">
                <SegmentedTabs
                  activeKey={activeSection}
                  counts={{
                    oversize: oversizeProducts.length,
                    box: boxProducts.length,
                    regular: regularProducts.length,
                  }}
                  onSelect={scrollToSection}
                />
              </div>
            </div>
          </div>
          {isTabsFixed && <div style={{ height: tabsHeight }} />}

          {/* Loading State */}
          {loading && <Spinner />}
          {/* Section: Oversize Fit */}
          {!loading && oversizeProducts.length > 0 && (
            <div ref={oversizeRef} className="space-y-3 scroll-mt-28">
              <h2 className="text-lg font-semibold text-black">Oversize Fit</h2>
              <ProductGrid
                products={oversizeProducts}
                onProductClick={handleProductClick}
                onFavoriteClick={handleFavoriteClick}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loading={loading}
              />
            </div>
          )}

          {/* Section: Boxy Fit */}
          {!loading && boxProducts.length > 0 && (
            <div ref={boxRef} className="space-y-3 scroll-mt-28">
              <h2 className="text-lg font-semibold text-black">Boxy Fit</h2>
              <ProductGrid
                products={boxProducts}
                onProductClick={handleProductClick}
                onFavoriteClick={handleFavoriteClick}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loading={loading}
              />
            </div>
          )}

          {/* Section: Regular Fit */}
          {!loading && regularProducts.length > 0 && (
            <div ref={regularRef} className="space-y-3 scroll-mt-28">
              <h2 className="text-lg font-semibold text-black">Regular Fit</h2>
              <ProductGrid
                products={regularProducts}
                onProductClick={handleProductClick}
                onFavoriteClick={handleFavoriteClick}
                onLoadMore={handleLoadMore}
                hasMore={hasMore}
                loading={loading}
              />
            </div>
          )}
          {/* Empty State */}
          {!loading && products.length === 0 && (
            <EmptyState
              searchQuery={searchQuery}
              selectedCategory={selectedCategory}
              onClearFilters={() => {
                setSearchQuery("");
                setSelectedCategory("all");
              }}
            />
          )}
        </div>
      </div>

      {/* Fixed bottom navigation */}
      <BottomNavigation />
    </div>
  );
};

// Local SegmentedTabs (same as NewArrivals variant)
function SegmentedTabs({
  activeKey,
  counts,
  onSelect,
}: {
  activeKey: "oversize" | "box" | "regular";
  counts: { oversize: number; box: number; regular: number };
  onSelect: (key: "oversize" | "box" | "regular") => void;
}) {
  const tabs = [
    { key: "oversize" as const, label: "Oversize", count: counts.oversize },
    { key: "box" as const, label: "Boxy Fit", count: counts.box },
    { key: "regular" as const, label: "Regular", count: counts.regular },
  ];
  const activeIndex = Math.max(0, tabs.findIndex((t) => t.key === activeKey));
  const widthPercent = 100 / tabs.length;
  return (
    <div role="tablist" aria-label="Store Sections" className="relative w-full select-none">
      <div className="grid grid-cols-3 text-sm font-medium text-gray-700 bg-gray-50 rounded-xl p-1 border border-gray-100">
        {tabs.map((tab) => {
          const isDisabled = tab.count === 0;
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              disabled={isDisabled}
              onClick={() => !isDisabled && onSelect(tab.key)}
              className={
                "relative py-2 text-center rounded-lg transition-colors " +
                (isDisabled
                  ? "text-gray-400"
                  : isActive
                  ? "text-primary-blue bg-white shadow-sm"
                  : "text-gray-700 hover:text-primary-blue")
              }
            >
              <span>{tab.label}</span>
              <span className="ml-1 text-xs opacity-70">{tab.count}</span>
            </button>
          );
        })}
      </div>
      <div
        className="pointer-events-none absolute -bottom-2 left-0 h-0.5 bg-primary-blue/80 transition-transform duration-300"
        style={{ width: `${widthPercent}%`, transform: `translateX(${activeIndex * 100}%)` }}
      />
    </div>
  );
}
